import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { STORAGE_KEYS } from "src/constants/common";
import storage from "src/helpers/storage";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { warmupStarted } from "src/redux/slices/cgmSlice";
import type { CgmDisplayStatus } from "src/constants/cgm";
import {
  CGM_DISPLAY_STATUS_CONFIG,
  CGM_SENSOR_CYCLE_DAYS,
} from "src/constants/cgm";
import type { CgmState, PocGlucoseJson } from "src/types/cgm.types";

export type CgmHookBadgeType = "success" | "warning" | "danger" | "info";

/** Status slice for CGM UI — map `code` to colors in the component if needed. */
export interface UseCgmStatus {
  code: CgmDisplayStatus;
  /** Machine id; `TRANSMITTER_DISCONNECTED` when raw is `TRANSMITTER_DISCONNECT`. */
  status: string;
  label: string;
  type: CgmHookBadgeType;
  rawConnectionStatus: string | null;
  transmitterOk: boolean;
}

/** Sensor dates from status/lifecycle API, live bind, and CGM program row. */
export interface UseCgmSensorInfo {
  startDate: string | null;
  endDate: string | null;
}

const BADGE_TYPES: Partial<Record<CgmDisplayStatus, CgmHookBadgeType>> = {
  CONNECTED: "success",
  WARMUP: "warning",
  BLUETOOTH_OFF: "warning",
  LOW_POWER: "warning",
  DISCONNECTED: "warning",
  TRANSMITTER_DISCONNECT: "danger",
  EXPIRED: "danger",
  TIMEOUT: "danger",
  NOT_CONNECTED: "danger",
  TEMPERATURE_ERROR: "danger",
  ERROR: "danger",
  SENSOR_MISMATCH: "danger",
  SYNCING: "info",
};

function mapRawConnectionToDisplay(
  raw: string | null | undefined
): CgmDisplayStatus | null {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim().toUpperCase();

  const aliases: Record<string, CgmDisplayStatus> = {
    CONNECTED: "CONNECTED",
    DISCONNECTED: "DISCONNECTED",
    EXPIRED: "EXPIRED",
    EXIPRED: "EXPIRED",
    TRANSMITTER_DISCONNECT: "TRANSMITTER_DISCONNECT",
    WEAK_SIGNAL: "LOW_POWER",
    ERROR_COMMON: "ERROR",
    BLUETOOTH_OFF: "BLUETOOTH_OFF",
  };

  if (aliases[s]) return aliases[s];
  if (s in CGM_DISPLAY_STATUS_CONFIG) return s as CgmDisplayStatus;
  return null;
}

function warmupPriorityState(cgm: CgmState): {
  active: boolean;
  minutesLeft: number;
} {
  const { step, displayStatus, warmupEndDate } = cgm;
  const warmupEndTime = warmupEndDate ? new Date(warmupEndDate).getTime() : 0;
  const isWarmupActive =
    !Number.isNaN(warmupEndTime) && warmupEndTime > Date.now();
  const active =
    isWarmupActive || step === "warmup" || displayStatus === "WARMUP";
  const remainingMs = isWarmupActive
    ? Math.max(0, warmupEndTime - Date.now())
    : 0;
  const minutesLeft = Math.floor(remainingMs / 60000);
  return { active, minutesLeft };
}

function lifecycleSensorStartDate(cgm: CgmState): string | null {
  const api = cgm.statusAndSensorLife.data?.[0] ?? null;
  return api?.startDate ?? cgm.cgmConnectionLive?.boundDevice?.bindDate ?? null;
}

function statusCardResolution(cgm: CgmState): {
  rawStatus: string | null;
  resolvedDisplay: CgmDisplayStatus;
  expiredByCycle: boolean;
} {
  const api = cgm.statusAndSensorLife.data?.[0] ?? null;
  const rawStatus =
    cgm.cgmConnectionLive?.connectionStatus ?? api?.status ?? null;
  let resolvedDisplay =
    mapRawConnectionToDisplay(rawStatus) ?? cgm.displayStatus;

  const warmup = warmupPriorityState(cgm);
  if (warmup.active) {
    resolvedDisplay = "WARMUP";
  }

  const start = lifecycleSensorStartDate(cgm);
  const expiredByCycle =
    start != null &&
    moment(start).isBefore(moment().subtract(CGM_SENSOR_CYCLE_DAYS, "days"));

  return { rawStatus, resolvedDisplay, expiredByCycle };
}

function transmitterOkFromResolution(r: {
  resolvedDisplay: CgmDisplayStatus;
  rawStatus: string | null;
  expiredByCycle: boolean;
}): boolean {
  if (
    r.resolvedDisplay === "EXPIRED" ||
    r.rawStatus === "EXPIRED" ||
    r.rawStatus === "EXIPRED" ||
    r.expiredByCycle
  ) {
    return false;
  }
  if (r.resolvedDisplay === "DISCONNECTED") return false;
  if (r.resolvedDisplay === "TRANSMITTER_DISCONNECT") return false;
  if (r.resolvedDisplay === "BLUETOOTH_OFF") return false;
  return true;
}

function machineStatusId(code: CgmDisplayStatus, raw: string | null): string {
  const u = raw?.trim().toUpperCase();
  if (code === "TRANSMITTER_DISCONNECT" || u === "TRANSMITTER_DISCONNECT") {
    return "TRANSMITTER_DISCONNECTED";
  }
  return code;
}

function buildUnifiedStatus(cgm: CgmState): UseCgmStatus {
  const r = statusCardResolution(cgm);
  const transmitterOk = transmitterOkFromResolution(r);

  let code: CgmDisplayStatus;
  let label: string;

  if (
    r.resolvedDisplay === "EXPIRED" ||
    r.rawStatus === "EXPIRED" ||
    r.rawStatus === "EXIPRED" ||
    r.expiredByCycle
  ) {
    code = "EXPIRED";
    label = CGM_DISPLAY_STATUS_CONFIG.EXPIRED.label;
  } else if (
    r.resolvedDisplay === "TRANSMITTER_DISCONNECT" ||
    r.rawStatus === "TRANSMITTER_DISCONNECT"
  ) {
    code = "TRANSMITTER_DISCONNECT";
    label = CGM_DISPLAY_STATUS_CONFIG.TRANSMITTER_DISCONNECT.label;
  } else if (r.resolvedDisplay === "DISCONNECTED") {
    code = "DISCONNECTED";
    label = CGM_DISPLAY_STATUS_CONFIG.DISCONNECTED.label;
  } else if (r.resolvedDisplay === "BLUETOOTH_OFF") {
    code = "BLUETOOTH_OFF";
    label = CGM_DISPLAY_STATUS_CONFIG.BLUETOOTH_OFF.label;
  } else {
    code = r.resolvedDisplay;
    if (code === "WARMUP") {
      const w = warmupPriorityState(cgm);
      label =
        w.minutesLeft > 0 ? `Warmup (${w.minutesLeft} min left)` : "Warmup";
    } else {
      label = CGM_DISPLAY_STATUS_CONFIG[code].label;
    }
  }

  const type = BADGE_TYPES[code] ?? ("info" as const);

  return {
    code,
    status: machineStatusId(code, r.rawStatus),
    label,
    type,
    rawConnectionStatus: r.rawStatus,
    transmitterOk,
  };
}

function getLatestReadingByTimeInMillis(
  readings: PocGlucoseJson[]
): PocGlucoseJson | null {
  if (readings.length === 0) return null;
  let best: PocGlucoseJson | null = null;
  let bestT = -Infinity;
  for (const r of readings) {
    const t = r.timeInMillis;
    if (typeof t !== "number" || Number.isNaN(t)) continue;
    if (t >= bestT) {
      bestT = t;
      best = r;
    }
  }
  return best;
}

const useCgm = () => {
  const dispatch = useAppDispatch();
  const cgm = useAppSelector((state) => state.cgm);
  const {
    statusAndSensorLife: { data: statusAndSensorLifeData },
  } = cgm;

  /** Hydrate Redux from storage when RN/API omitted `warmupEndDate` (insights + device card). */
  useEffect(() => {
    const w = cgm.warmupEndDate;
    if (w) {
      const t = new Date(w).getTime();
      if (Number.isFinite(t) && t > Date.now()) return;
    }
    const stored = storage.getItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    if (!stored) return;
    const t = new Date(stored).getTime();
    if (!Number.isFinite(t) || t <= Date.now()) return;
    dispatch(warmupStarted(stored));
  }, [cgm.warmupEndDate, dispatch]);

  /**
   * Backend `/cgm/status` often reports CONNECTED while the sensor is still warming; Redux may also
   * miss `warmupEndDate` briefly. Merge future end time from local storage (same key as journey)
   * so badges and insights match the CGM status screen.
   */
  const effectiveCgm = useMemo((): CgmState => {
    const w = cgm.warmupEndDate;
    if (w) {
      const t = new Date(w).getTime();
      if (Number.isFinite(t) && t > Date.now()) return cgm;
    }
    const stored = storage.getItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    if (stored) {
      const t = new Date(stored).getTime();
      if (Number.isFinite(t) && t > Date.now()) {
        return { ...cgm, warmupEndDate: stored };
      }
    }
    return cgm;
  }, [cgm]);

  const status = useMemo(
    () => buildUnifiedStatus(effectiveCgm),
    [effectiveCgm]
  );

  const warmupActive = useMemo(
    () => warmupPriorityState(effectiveCgm).active,
    [effectiveCgm]
  );

  /** Re-render once per second while warmup is active so countdown (e.g. bottom sheet badge) updates. */
  const [warmupTick, setWarmupTick] = useState(0);
  useEffect(() => {
    if (!warmupActive) return;
    const id = window.setInterval(() => setWarmupTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [warmupActive]);

  const warmupCountdownMMSS = useMemo(() => {
    const w = warmupPriorityState(effectiveCgm);
    if (!w.active) return null;
    const end = effectiveCgm.warmupEndDate
      ? new Date(effectiveCgm.warmupEndDate).getTime()
      : NaN;
    if (!Number.isFinite(end) || end <= Date.now()) return null;
    const ms = end - Date.now();
    const totalSec = Math.floor(ms / 1000);
    const mm = Math.floor(totalSec / 60);
    const ss = totalSec % 60;
    return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }, [effectiveCgm, warmupTick]);

  const sensorInfo = useMemo((): UseCgmSensorInfo => {
    const row = cgm.statusAndSensorLife.data?.[0];
    return {
      startDate: row?.startDate ?? null,
      endDate: row?.endDate ?? null,
    };
  }, [cgm]);

  const liveReadings = useMemo(() => {
    if (cgm.latestReadings.length > 0) return cgm.latestReadings;
    return cgm.cgmConnectionLive?.readings ?? [];
  }, [cgm.latestReadings, cgm.cgmConnectionLive?.readings]);

  const latestFromLive = useMemo(
    () => getLatestReadingByTimeInMillis(liveReadings),
    [liveReadings]
  );

  const glucoseFromLive =
    latestFromLive != null
      ? latestFromLive.showGlucoseMG ?? latestFromLive.showGlucose
      : null;

  const apiGlucose =
    statusAndSensorLifeData?.[0]?.lastSyncCGMData?.glucoseValue ?? null;
  const apiReadingTimestamp =
    statusAndSensorLifeData?.[0]?.lastSyncCGMData?.readingTimestamp ?? null;

  console.log("statusAndSensorLifeData", statusAndSensorLifeData);
  console.log("cgm.currentGlucose", cgm.currentGlucose);
  console.log("glucoseFromLive", glucoseFromLive);
  console.log("apiGlucose", apiGlucose);

  const displayGlucose =
    status.code === "WARMUP" && warmupCountdownMMSS
      ? "--"
      : glucoseFromLive ?? cgm.currentGlucose ?? apiGlucose;

  const displayTimestampIso = useMemo(() => {
    if (status.code === "WARMUP" && warmupCountdownMMSS) return null;
    if (latestFromLive?.timeInMillis != null) {
      const ms = latestFromLive.timeInMillis;
      if (typeof ms === "number" && Number.isFinite(ms)) {
        return new Date(ms).toISOString();
      }
    }
    if (typeof apiReadingTimestamp === "string" && apiReadingTimestamp) {
      return apiReadingTimestamp;
    }
    return null;
  }, [latestFromLive?.timeInMillis, apiReadingTimestamp]);

  return {
    status,
    sensorInfo,
    glucoseValue: displayGlucose,
    timestamp: displayTimestampIso,
    warmupCountdownMMSS,
  };
};

export default useCgm;
